import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";

import {
  useGenerateAccountHistoryFromOrionMutation,
  useGenerateOptionCoverageMutation,
} from "features/trading/accounts";
import { NOTISTACK_FAILURE_OPTIONS } from "constants/index";
import { BalanceTypeEnum } from "helpers/types";

const GENERATION_ERROR_MESSAGE: Record<BalanceTypeEnum, string> = {
  [BalanceTypeEnum.TRADE_AMOUNT]:
    "Couldn't create trade amount history for this account.",
  [BalanceTypeEnum.PORTFOLIO]:
    "Couldn't generate portfolio history for this account.",
};

export interface AccountHistoryGeneration {
  generate: (balanceType: BalanceTypeEnum) => Promise<void>;
  isGeneratingTradeAmount: boolean;
  isGeneratingPortfolio: boolean;
}

export const useAccountHistoryGeneration = (
  accountId: number | null | undefined,
  strategyId: number | null | undefined,
): AccountHistoryGeneration => {
  const [generateOptionCoverage, { isLoading: isGeneratingTradeAmount }] =
    useGenerateOptionCoverageMutation();
  const [generateFromOrion, { isLoading: isGeneratingPortfolio }] =
    useGenerateAccountHistoryFromOrionMutation();

  const generate = useCallback(
    async (balanceType: BalanceTypeEnum) => {
      if (!accountId || !strategyId) return;
      const payload = { account_id: accountId, strategy_id: strategyId };
      try {
        if (balanceType === BalanceTypeEnum.TRADE_AMOUNT) {
          await generateOptionCoverage(payload).unwrap();
        } else {
          await generateFromOrion(payload).unwrap();
        }
      } catch {
        enqueueSnackbar(
          GENERATION_ERROR_MESSAGE[balanceType],
          NOTISTACK_FAILURE_OPTIONS,
        );
      }
    },
    [accountId, strategyId, generateOptionCoverage, generateFromOrion],
  );

  return {
    generate,
    isGeneratingTradeAmount,
    isGeneratingPortfolio,
  };
};
